import type { AxiosResponse } from 'axios';

/**
 * Collapses concurrent identical GETs into a single HTTP request.
 *
 * Ten components call `projectsAPI.list()`. The Dashboard shell is always mounted (it
 * populates the sidebar) and each routed page fetches for itself, so navigating to
 * /portal/home issued `GET /api/projects/` twice — measured on the production build, at
 * roughly 450 ms per request against a cross-region backend.
 *
 * This deliberately does NOT cache responses. Only requests that are still in flight are
 * shared, and the entry is dropped the moment one settles, so a caller can never receive
 * data that was already stale when it asked. That makes it safe to apply globally without
 * any invalidation strategy — which a response cache would require, and which is really
 * the job of a proper data layer (TanStack Query) when the app gets one.
 *
 * A short window is applied on top so a request starting a few milliseconds after the
 * first still joins it: two components mounting in the same navigation rarely fire on
 * exactly the same tick.
 */

/** How long a settled entry stays joinable, in milliseconds. */
const SETTLE_WINDOW_MS = 250;

interface Entry {
  promise: Promise<AxiosResponse<unknown>>;
  /** Set when the request settles; used to expire the entry after the window. */
  settledAt?: number;
}

const inFlight = new Map<string, Entry>();

function isJoinable(entry: Entry): boolean {
  if (entry.settledAt === undefined) return true; // still in flight
  return Date.now() - entry.settledAt < SETTLE_WINDOW_MS;
}

/**
 * Run `request` unless an identical one is already in flight (or settled within the
 * window), in which case share that one.
 */
export function dedupeGet<T>(key: string, request: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
  const existing = inFlight.get(key);
  if (existing && isJoinable(existing)) {
    return existing.promise as Promise<AxiosResponse<T>>;
  }

  const entry: Entry = { promise: undefined as unknown as Promise<AxiosResponse<unknown>> };
  const promise = request()
    .then((res) => {
      entry.settledAt = Date.now();
      // Drop the entry once the window closes so later callers always refetch.
      setTimeout(() => {
        if (inFlight.get(key) === entry) inFlight.delete(key);
      }, SETTLE_WINDOW_MS);
      return res;
    })
    .catch((err) => {
      // Never share a failure beyond the callers already waiting on it.
      inFlight.delete(key);
      throw err;
    });

  entry.promise = promise as Promise<AxiosResponse<unknown>>;
  inFlight.set(key, entry);
  return promise;
}

/** Test helper: forget everything currently tracked. */
export function resetDedupe(): void {
  inFlight.clear();
}
